/* eslint-disable jira/filename-case */
import { Component, type ReactNode, type ReactElement } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import type { AdditionalEventAttributes } from '@jira/platform__experience-tracking-analytics/src/index.tsx';
import { isClientFetchError } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import type { ExperienceContext } from '../../common/context/index.tsx';
import type { TrackerCallbacks } from '../../common/types.tsx';

type Props = {
	experienceTrackerId: string;
	location: string;
	children: ReactNode | ((arg1: TrackerCallbacks) => ReactElement | null);
	failureEventAttributes: AdditionalEventAttributes | null;
	experienceContext: ExperienceContext;
	parentProvidersWhitelist: string[] | null;
};
/**
 * Serves as a foundational component designed to track and report various events (success, failure, abort) in an application without impacting the host application's UI or error handling. It ensures each event is signaled once per reporting period and manages context based on the current reporting provider and experience state.
 */
export default class TrackerBase extends Component<Props> {
	constructor(props: Props) {
		super(props);
		this.resetTracker();
	}

	componentDidCatch(error: Error): void {
		if (!isClientFetchError(error)) {
			this.onFailure(undefined, {
				errorMessage: error.message,
			});
		}
		// We're deliberately rethrowing the error here because we don't want to interfere with
		// any error handling the container app may have in place. This app exists only to track
		// error/success metrics; we don't want to interfere with the app that's actually rendering UI.
		throw error;
	}

	componentWillUnmount(): void {
		// This guards against scenarios where the experience tracker has an unstable parent.
		// With concurrent rendering, it's possible that this component was rendered, but never "committed",
		// in this scenario, we would have called onPending for a version of this component at would never succeed OR fail.
		if (fg('jfp-magma-fix-experience-tracker-concurrency')) {
			this.props.experienceContext.consumerCallbacks.onClearPending(this.props.experienceTrackerId);
		}
	}

	onSuccess = (): void => {
		// This guards against the possibility of the child calling `onExperienceSuccess` repeatedly.
		if (!this.hasSignalledSuccess && this.isReportingToCurrentProvider()) {
			this.hasSignalledSuccess = true;
			this.props.experienceContext.consumerCallbacks.onSuccess(this.props.experienceTrackerId);
		}
	};

	onFailure = (
		locationSuffix?: string,
		extraFailureEventAttributes?: AdditionalEventAttributes,
	): void => {
		const {
			experienceContext: {
				consumerCallbacks: { onFailure },
			},
			location,
			failureEventAttributes,
		} = this.props;
		if (!this.hasSignalledFailure && this.isReportingToCurrentProvider()) {
			this.hasSignalledFailure = true;
			const concatenatedLocation =
				locationSuffix == null ? location : `${location}.${locationSuffix}`;
			const mergedFailureAttributes = {
				...extraFailureEventAttributes,
				...failureEventAttributes,
			};
			onFailure(concatenatedLocation, mergedFailureAttributes);
		}
	};

	onAbort = (
		locationSuffix?: string,
		extraFailureEventAttributes?: AdditionalEventAttributes,
	): void => {
		const {
			experienceContext: {
				consumerCallbacks: { onAbort },
			},
			location,
			failureEventAttributes,
		} = this.props;
		if (!this.hasSignalledFailure && this.isReportingToCurrentProvider()) {
			this.hasSignalledFailure = true;
			const concatenatedLocation =
				locationSuffix == null ? location : `${location}.${locationSuffix}`;
			const mergedFailureAttributes = {
				...extraFailureEventAttributes,
				...failureEventAttributes,
			};
			onAbort(concatenatedLocation, mergedFailureAttributes);
		}
	};

	isReportingToCurrentProvider(): boolean {
		const { experience } = this.props.experienceContext;
		return (
			// parentProviders is an OPTIONAL whitelist. If not specified, then we are definitely reporting to the current experience
			this.parentProvidersWhitelistSet == null ||
			// otherwise make sure we are currently in one of the listed experiences
			this.parentProvidersWhitelistSet.has(experience)
		);
	}

	// @ts-expect-error - TS2564 - Property 'hasSignalledSuccess' has no initializer and is not definitely assigned in the constructor.
	hasSignalledSuccess: boolean;

	// @ts-expect-error - TS2564 - Property 'hasSignalledFailure' has no initializer and is not definitely assigned in the constructor.
	hasSignalledFailure: boolean;

	// @ts-expect-error - TS2564 - Property 'parentProvidersWhitelistSet' has no initializer and is not definitely assigned in the constructor.
	parentProvidersWhitelistSet: Set<string> | null;

	lastExperienceId: string | null | undefined;

	// Resetting must not call setState since this can be executed in the render phase
	resetTracker(): void {
		this.hasSignalledFailure = false;
		this.hasSignalledSuccess = false;
		this.lastExperienceId = this.props.experienceContext.experienceId;

		// Must only respect updates to `parentProvidersWhitelist` when the experience resets, otherwise weird bugs can arise.
		// Eg: a component could call onSuccess when it never called onPending, this would result in premature experience successes.
		this.parentProvidersWhitelistSet = this.props.parentProvidersWhitelist
			? new Set(this.props.parentProvidersWhitelist)
			: null;
		this.isReportingToCurrentProvider() &&
			this.props.experienceContext.consumerCallbacks.onPending(this.props.experienceTrackerId);
	}

	// Check every render to reset the experience state instance variables
	checkExperienceIdForExperienceReset(): void {
		if (this.lastExperienceId !== this.props.experienceContext.experienceId) {
			this.resetTracker();
		}
	}

	render(): ReactNode {
		this.checkExperienceIdForExperienceReset();
		const { children } = this.props;
		return typeof children === 'function'
			? children({
					onExperienceSuccess: this.onSuccess,
					onExperienceFailure: this.onFailure,
					onExperienceAbort: this.onAbort,
				})
			: children;
	}
}
